import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Module } from "@bios/types";
import { Button, Card, Pill, SignalNoiseChart, StatCard } from "@bios/ui";
import { getModule } from "@bios/taxonomies";
import { useApiAuthed, useApiDev } from "../auth.js";
import type {
  AccountabilityActor,
  DashboardResponse,
  ModuleImpactSlice,
  OverviewResponse,
  SystemLink,
} from "../api.js";
import { InsightCard } from "./Alerts.js";

const MODULE_HEADLINE: Record<string, { title: string; sub: string }> = {
  media: {
    title: "What's actually happening in Black media — not what it looks like.",
    sub: "Live analysis of real activity vs recycled content, PR, and noise.",
  },
  capital: {
    title: "Where capital is actually moving — not where it's being announced.",
    sub: "Track real funding activity vs headlines, PR, and perceived momentum.",
  },
  education: {
    title: "What's actually happening in Black education — not what gets announced.",
    sub: "Real outcomes vs press releases across HBCUs, K-12, and accreditation.",
  },
  policy: {
    title: "What policy is actually doing — not what's being announced.",
    sub: "Enacted legislation, disbursed funding, and outcomes — not proposals.",
  },
  workforce: {
    title: "What is changing for Black workers — not what programs claim.",
    sub: "Hiring, layoffs, placement, wages, entrepreneurship, and job quality.",
  },
  legal: {
    title: "What legal action is changing outcomes — not what is being filed.",
    sub: "Court decisions, enforcement, settlements, rights, and justice outcomes.",
  },
};

// Phase 20.3 Fix 6 + Fix 7: per-module zero-impact intelligence copy.
// Each module gets its own headline, subline, root cause, and priority
// action when measurable Black impact in that module is zero.
interface ModuleZeroCopy {
  headline: string;
  subline: string;
  root_cause: string;
  priority_action: string;
}
const MODULE_ZERO_COPY: Record<string, ModuleZeroCopy> = {
  media: {
    headline: "Media attention is not translating into measurable Black impact.",
    subline:
      "Coverage is producing narrative volume, but not verified real-world change.",
    root_cause:
      "Media attention is not translating into outcomes because coverage is driven by narrative rather than measurable action.",
    priority_action:
      "Deprioritize narrative-heavy coverage until capital, policy, or outcome evidence appears.",
  },
  capital: {
    headline: "Capital activity is not reaching Black communities.",
    subline:
      "Funding signals exist, but deployment to Black-owned or Black-serving destinations is not confirmed.",
    root_cause:
      "Capital is not reaching Black communities because funding destinations are general, unknown, or not confirmed as Black-serving.",
    priority_action: "Reallocate attention toward confirmed deployment targets.",
  },
  education: {
    headline:
      "Education activity is not producing measurable Black student outcomes.",
    subline: "Institutional activity exists, but outcome evidence is missing.",
    root_cause:
      "Education activity is not producing outcomes because access, completion, funding, or workforce results are not yet observed.",
    priority_action:
      "Validate immediately whether programs are improving access, completion, funding, or workforce outcomes.",
  },
  policy: {
    headline: "Policy activity is not producing measurable Black outcomes.",
    subline:
      "Government action exists, but implementation, enforcement, or outcome evidence is missing.",
    root_cause:
      "Policy activity is not producing outcomes because implementation, enforcement, or funding distribution is not occurring.",
    priority_action:
      "Validate immediately where policy action is failing to convert into funded or enforced outcomes.",
  },
  workforce: {
    headline: "Workforce activity is not producing measurable Black outcomes.",
    subline:
      "Hiring, training, or wage activity exists, but placement, retention, and wage outcomes are missing.",
    root_cause:
      "Workforce activity is not producing outcomes because job placement, retention, or wage evidence is not being reported.",
    priority_action:
      "Validate immediately which employers, programs, or training providers own the missing outcome data.",
  },
  legal: {
    headline: "Legal activity is not producing measurable Black outcomes.",
    subline:
      "Lawsuits, investigations, or settlements exist, but remedies and reforms are not confirmed.",
    root_cause:
      "Legal activity is not producing outcomes because enforcement, remedy, reform, or accountability evidence is missing.",
    priority_action:
      "Validate immediately which court, agency, or legal actor owns the missing remedy.",
  },
};

function moduleQuery(m: string): string {
  switch (m) {
    case "media":
      return "Which media coverage of Black communities is high narrative but low reality?";
    case "capital":
      return "Where is capital actually flowing to Black-owned or Black-serving destinations?";
    case "education":
      return "What outcomes are recorded at HBCUs and heavy-Black school districts in this window?";
    case "policy":
      return "Which Black-relevant policy items are funded or deployed but show no outcome evidence?";
    case "workforce":
      return "Which Black workforce signals have no placement, retention, or wage outcome?";
    case "legal":
      return "Which legal actions affecting Black communities produced enforceable outcomes?";
    default:
      return "What is affecting Black communities right now?";
  }
}

interface PageProps {
  devModeNoAuth?: boolean;
}

export function Dashboard({ devModeNoAuth }: PageProps) {
  const { module = "media" } = useParams<{ module: Module }>();
  const m = module as Module;
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    api
      .dashboard(m)
      .then(setData)
      .catch((e) => setError(String(e)));
  }, [api, m]);

  // Phase 20.3: pull /v1/overview for the module's Black-impact slice.
  // Cached server-side (5 min) so this is essentially free on warm hits.
  useEffect(() => {
    let alive = true;
    api
      .overview("7d")
      .then((d) => {
        if (alive) setOverview(d);
      })
      .catch(() => {
        /* module intelligence header is optional; degrade silently */
      });
    return () => {
      alive = false;
    };
  }, [api]);

  const headline = MODULE_HEADLINE[m] ?? {
    title: getModule(m)?.label ?? m,
    sub: getModule(m)?.tagline ?? "",
  };

  const moduleSlice =
    overview?.system_impact?.modules?.find((s) => s.module === m) ?? null;

  return (
    <>
      <div className="bios-page-header">
        <div>
          <h1 className="bios-page-title">{headline.title}</h1>
          <p className="bios-page-sub">{headline.sub}</p>
        </div>
      </div>

      {error && (
        <Card>
          <p style={{ color: "var(--bios-noise)", fontSize: 13 }}>API error: {error}</p>
        </Card>
      )}

      {!data && !error && <Card><p style={{ color: "var(--bios-text-muted)" }}>Loading…</p></Card>}

      {data && data.total_items === 0 && (
        <ModuleEmptyState module={m} devModeNoAuth={devModeNoAuth} />
      )}

      {data && data.total_items > 0 && (
        <>
          {/* Phase 20.3 Fix 5–7: Module Intelligence Layer.
              Order: interpretation -> root cause -> priority action ->
              (coverage, if incomplete) -> KPIs -> trend -> insights. */}
          <ModuleIntelligenceHeader
            module={m}
            slice={moduleSlice}
            accountabilitySummary={overview?.accountability?.summary ?? null}
            actors={
              overview?.accountability?.actors?.filter((a) => a.module === m) ?? []
            }
            causality={
              overview?.system_causality?.system_links?.filter((l) =>
                l.modules.includes(m),
              ) ?? []
            }
          />

          {overview?.coverage?.is_incomplete && (
            <CoverageNote pct={overview.coverage.unknown_pct} />
          )}

          {/* Phase 20.3 Fix 8: dominant labels reflect Black impact.
              Reality / classified counts are still shown, but Black-impact
              KPIs lead. */}
          <div className="bios-grid cols-4" style={{ marginBottom: 16 }}>
            <StatCard
              label="Measurable Black Impact"
              value={moduleSlice ? moduleSlice.measurable_items.toLocaleString() : "—"}
              sub={
                moduleSlice
                  ? `${moduleSlice.measurable_pct}% of ${moduleSlice.total_items} items in window`
                  : "Awaiting classification"
              }
            />
            <StatCard
              label="Black-Relevant Signals"
              value={
                moduleSlice
                  ? moduleSlice.black_relevant_items.toLocaleString()
                  : "—"
              }
              sub={
                moduleSlice && moduleSlice.total_items > 0
                  ? `${Math.round(
                      (moduleSlice.black_relevant_items /
                        moduleSlice.total_items) *
                        100,
                    )}% of items in window`
                  : "Black-direct or disproportionate"
              }
              trend="up"
            />
            <StatCard
              label="Activity Without Outcome"
              value={
                moduleSlice
                  ? Math.max(
                      0,
                      moduleSlice.black_relevant_items -
                        moduleSlice.measurable_items,
                    ).toLocaleString()
                  : data.noise_items.toLocaleString()
              }
              sub="Black-relevant, but stalled at announced/active"
              trend="down"
            />
            <StatCard
              label="Items analyzed"
              value={data.total_items.toLocaleString()}
              sub="Total items in this module"
            />
          </div>

          <RealityLegend />

          <div className="bios-grid cols-3" style={{ marginBottom: 16 }}>
            <Card title="Real Activity vs Narrative (14d)" className="span-2" >
              <div style={{ minWidth: 0 }}>
                <SignalNoiseChart
                  data={data.signal_noise_series.map((p) => ({ date: p.date, signal: p.signal, noise: p.noise }))}
                />
              </div>
            </Card>
            <Card title="Top topics">
              {data.top_topics.length === 0 ? (
                <p style={{ color: "var(--bios-text-muted)", fontSize: 13 }}>No classified items yet.</p>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {data.top_topics.map((t) => (
                    <Pill key={t.topic}>
                      {t.topic} <span style={{ marginLeft: 6, color: "var(--bios-text-faint)" }}>{t.count}</span>
                    </Pill>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div className="bios-grid cols-2">
            <div style={{ display: "grid", gap: 10, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: "var(--bios-text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Recent insights
              </div>
              {data.recent_alerts.length === 0 ? (
                <Card>
                  <p style={{ color: "var(--bios-text-muted)", fontSize: 13, margin: 0 }}>
                    Quiet — no high-signal insights.
                  </p>
                </Card>
              ) : (
                data.recent_alerts.map((a) => <InsightCard key={a.id} alert={a} />)
              )}
            </div>
            <Card title="Pipeline status">
              <div style={{ display: "grid", gap: 12 }}>
                <PipelineRow label="Sources active" value="ok" />
                <PipelineRow label="Ingestion queue" value="ok" />
                <PipelineRow label="Classification" value={data.classified_items > 0 ? "ok" : "idle"} />
                <PipelineRow label="Scoring" value={data.signal_items + data.noise_items > 0 ? "ok" : "idle"} />
              </div>
            </Card>
          </div>

          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <ContentTypeBreakdown breakdown={data.content_type_breakdown} classified={data.classified_items} />
          </div>

          <div style={{ marginTop: 0 }}>
            <Card title="How this works">
              <p style={{ margin: 0, fontSize: 13, color: "var(--bios-text-muted)", lineHeight: 1.6 }}>
                We ingest content → classify it → filter noise → score reality → surface what's actually happening.
              </p>
            </Card>
          </div>
        </>
      )}
    </>
  );
}

const CONTENT_TYPE_ORDER = [
  "ORIGINAL_REPORTING",
  "AGGREGATED",
  "PR_SPONSORED",
  "OPINION",
  "GOVERNMENT_NONPROFIT",
  "UNCLASSIFIED",
];

const CONTENT_TYPE_LABEL: Record<string, string> = {
  ORIGINAL_REPORTING: "Original reporting",
  AGGREGATED: "Aggregated",
  PR_SPONSORED: "PR / sponsored",
  OPINION: "Opinion",
  GOVERNMENT_NONPROFIT: "Government / nonprofit",
  UNCLASSIFIED: "Unclassified",
};

const CONTENT_TYPE_COLOR: Record<string, string> = {
  ORIGINAL_REPORTING: "var(--bios-signal)",
  AGGREGATED: "var(--bios-info)",
  PR_SPONSORED: "var(--bios-noise)",
  OPINION: "var(--bios-mixed)",
  GOVERNMENT_NONPROFIT: "var(--bios-accent)",
  UNCLASSIFIED: "var(--bios-text-faint)",
};

function ContentTypeBreakdown({
  breakdown,
  classified,
}: {
  breakdown: { content_type: string; count: number }[];
  classified: number;
}) {
  const map = new Map(breakdown.map((b) => [b.content_type, b.count]));
  const ordered = CONTENT_TYPE_ORDER.map((ct) => ({ content_type: ct, count: map.get(ct) ?? 0 }));
  const total = ordered.reduce((sum, r) => sum + r.count, 0) || classified || 1;

  return (
    <Card title="Content type breakdown">
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--bios-text-muted)" }}>
        How items in this module break down by what kind of journalism they are.
      </p>
      <div
        style={{
          display: "flex",
          height: 10,
          borderRadius: 6,
          overflow: "hidden",
          background: "var(--bios-border)",
          marginBottom: 12,
        }}
      >
        {ordered.map((r) =>
          r.count === 0 ? null : (
            <div
              key={r.content_type}
              title={`${CONTENT_TYPE_LABEL[r.content_type]}: ${r.count}`}
              style={{
                width: `${(r.count / total) * 100}%`,
                background: CONTENT_TYPE_COLOR[r.content_type],
              }}
            />
          ),
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 8 }}>
        {ordered.map((r) => (
          <div key={r.content_type} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: CONTENT_TYPE_COLOR[r.content_type],
                opacity: r.count === 0 ? 0.3 : 1,
              }}
            />
            <span style={{ color: "var(--bios-text-muted)" }}>{CONTENT_TYPE_LABEL[r.content_type]}</span>
            <span style={{ marginLeft: "auto", fontFamily: "var(--bios-mono)" }}>
              {r.count} <span style={{ color: "var(--bios-text-faint)" }}>· {((r.count / total) * 100).toFixed(0)}%</span>
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RealityHook({
  module,
  signal,
  classified,
  total,
}: {
  module: Module;
  signal: number;
  classified: number;
  total: number;
}) {
  const denom = classified > 0 ? classified : total;
  const realPct = denom > 0 ? Math.round((signal / denom) * 100) : 0;
  const space =
    {
      media: "Black media",
      capital: "Black capital",
      education: "Black education",
      policy: "Black policy",
      workforce: "Black workforce",
    }[module as string] ?? getModule(module)?.label ?? module;
  return (
    <div style={{ marginBottom: 16 }}>
      <Card>
        <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--bios-text-faint)", marginBottom: 6 }}>
          Reality check
        </div>
        <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.3 }}>
          Only <span style={{ color: "var(--bios-signal)" }}>{realPct}%</span> of activity in {space} is real.
        </div>
        <div style={{ fontSize: 13, color: "var(--bios-text-muted)", marginTop: 6 }}>
          The rest is recycled content, press releases, or perceived momentum.
        </div>
      </Card>
    </div>
  );
}

function ModuleEmptyState({
  module,
  devModeNoAuth,
}: {
  module: Module;
  devModeNoAuth?: boolean;
}) {
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const [sources, setSources] = useState<{ id: string; name: string; module: string }[] | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .sources(module)
      .then((d) => alive && setSources(d.sources.map((s) => ({ id: s.id, name: s.name, module: s.module }))))
      .catch(() => alive && setSources([]));
    return () => {
      alive = false;
    };
  }, [api, module]);

  async function triggerAll() {
    if (!sources) return;
    setTriggering(true);
    setMsg(null);
    try {
      let ok = 0;
      for (const s of sources) {
        try {
          await api.triggerSource(s.id);
          ok += 1;
        } catch {
          /* continue */
        }
      }
      setMsg(`Triggered ${ok} of ${sources.length} sources. Items appear in 1–3 minutes.`);
    } finally {
      setTriggering(false);
    }
  }

  const hasSources = (sources?.length ?? 0) > 0;
  const moduleLabel = getModule(module)?.label ?? module;

  return (
    <Card>
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--bios-text-faint)" }}>
          {moduleLabel}
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.35 }}>
          {hasSources
            ? `${moduleLabel} sources registered, but no signals yet.`
            : `No ${module} sources registered yet.`}
        </div>
        <div style={{ fontSize: 13, color: "var(--bios-text-muted)", lineHeight: 1.5 }}>
          {hasSources
            ? "Trigger ingestion to pull from each source. Items will be classified, scored, and indexed across modules."
            : "Seed the curated source list to bootstrap this module. You can edit or trigger individual sources after seeding."}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingTop: 4 }}>
          {hasSources ? (
            <Button variant="primary" onClick={triggerAll} disabled={triggering}>
              {triggering ? "Triggering…" : `Trigger ingestion (${sources?.length ?? 0})`}
            </Button>
          ) : (
            <Link to={`/app/admin/seed/${module}`}>
              <Button variant="primary">Seed {moduleLabel} sources</Button>
            </Link>
          )}
          <Link to={`/app/${module}/sources`}>
            <Button>Open source registry</Button>
          </Link>
        </div>
        {msg && (
          <div style={{ fontSize: 12, color: "var(--bios-signal)" }}>{msg}</div>
        )}
      </div>
    </Card>
  );
}

// Phase 20.3 Fix 5–7 + Fix 9: Module Intelligence Layer header.
// Sits above KPIs. Speaks first; uncertainty is admitted afterward
// via the CoverageNote component. When measurable Black impact in
// this module is zero, renders the module-specific zero-impact copy
// (interpretation + root cause + priority action). Otherwise it
// renders a compact Black-impact summary with the same shape.
function ModuleIntelligenceHeader({
  module,
  slice,
  accountabilitySummary,
  actors,
  causality,
}: {
  module: Module;
  slice: ModuleImpactSlice | null;
  accountabilitySummary: string | null;
  actors: AccountabilityActor[];
  causality: SystemLink[];
}) {
  const zeroCopy = MODULE_ZERO_COPY[module];
  if (!slice) return null;

  const measurable = slice.measurable_items;
  const isZero = measurable === 0;
  const headline = isZero && zeroCopy
    ? zeroCopy.headline
    : `Measured Black impact in ${module} is ${measurable === 1 ? "1 item" : `${measurable} items`} (${slice.measurable_pct}%).`;
  const subline = isZero && zeroCopy ? zeroCopy.subline : null;
  const rootCause = isZero && zeroCopy ? zeroCopy.root_cause : null;
  const priority = isZero && zeroCopy
    ? zeroCopy.priority_action
    : `Prioritize the ${slice.black_relevant_items} Black-relevant signal${slice.black_relevant_items === 1 ? "" : "s"} that have not yet reached outcome.`;

  return (
    <div className={`bios-modint${isZero ? " bios-modint-zero" : ""}`}>
      <div className="bios-modint-eyebrow">Module intelligence</div>
      <div className="bios-modint-headline">{headline}</div>
      {subline && <div className="bios-modint-sub">{subline}</div>}
      <div className="bios-modint-scope">
        {slice.measurable_items} of {slice.total_items} {module} items reached
        measurable Black impact.
      </div>

      {accountabilitySummary && (
        <div className="bios-modint-section">
          <div className="bios-modint-section-label">Accountability</div>
          <div className="bios-modint-section-body">{accountabilitySummary}</div>
        </div>
      )}

      {rootCause && (
        <div className="bios-modint-section">
          <div className="bios-modint-section-label">Why</div>
          <div className="bios-modint-section-body">{rootCause}</div>
        </div>
      )}

      {causality.length > 0 && (
        <div className="bios-modint-section">
          <div className="bios-modint-section-label">Causality</div>
          <ul className="bios-modint-list">
            {causality.slice(0, 2).map((l, i) => (
              <li key={i} className="bios-modint-list-item">{l.statement}</li>
            ))}
          </ul>
        </div>
      )}

      {actors.length > 0 && (
        <div className="bios-modint-section">
          <div className="bios-modint-section-label">Actors</div>
          <ul className="bios-modint-list">
            {actors.slice(0, 4).map((a, i) => (
              <li key={i} className="bios-modint-list-item">
                <strong>{a.name}</strong>{" "}
                <span style={{ color: "var(--bios-text-faint)", fontSize: 12 }}>
                  · {ROLE_LABEL[a.role] ?? a.role}
                </span>
                <div style={{ fontSize: 12, color: "var(--bios-text-muted)" }}>
                  {a.involvement}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bios-modint-section">
        <div className="bios-modint-section-label">Priority action</div>
        <div className="bios-modint-section-body bios-modint-priority">
          {priority}
        </div>
      </div>

      <Link
        to={`/app/command?q=${encodeURIComponent(moduleQuery(module))}`}
        className="bios-modint-cta"
      >
        Investigate in Command Center →
      </Link>
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = {
  funder: "Funder",
  policy_actor: "Policy actor",
  institution: "Institution",
  amplifier: "Amplifier",
  originator: "Originator",
};

function CoverageNote({ pct }: { pct: number }) {
  return (
    <div className="bios-coverage-banner" role="status" style={{ marginBottom: 16 }}>
      <span className="bios-coverage-banner-icon" aria-hidden>◌</span>
      <div>
        <strong>Impact classification incomplete.</strong> {pct}% of items in
        this window have not been mapped to population impact yet. Numbers
        below reflect classified items only.
      </div>
    </div>
  );
}

function RealityLegend() {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 16,
        margin: "0 0 16px",
        padding: "10px 14px",
        border: "1px solid var(--bios-border)",
        borderRadius: 8,
        fontSize: 12,
        color: "var(--bios-text-muted)",
      }}
    >
      <span style={{ color: "var(--bios-text-faint)", letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 11 }}>
        Reality Score
      </span>
      <LegendDot color="var(--bios-signal)" label="80–100 · Real activity" />
      <LegendDot color="var(--bios-mixed)" label="50–79 · Mixed" />
      <LegendDot color="var(--bios-noise)" label="0–49 · Mostly narrative" />
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
      {label}
    </span>
  );
}

function pct(n: number, d: number): string {
  if (!d) return "0";
  return ((n / d) * 100).toFixed(0);
}

function PipelineRow({ label, value }: { label: string; value: "ok" | "idle" | "error" }) {
  const variant = value === "ok" ? "signal" : value === "error" ? "noise" : "mixed";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: 13, color: "var(--bios-text-muted)" }}>{label}</span>
      <Pill variant={variant}>{value}</Pill>
    </div>
  );
}
